// src/pages/admin/AdminKits.tsx
//
// The yearly hardware package. Every online/hybrid learner is posted one box
// a year. Two tabs:
//   Shipments    — every child's box: address, packing, courier, tracking.
//   Kit contents — what goes in each age group's box (and what it costs).

import { useEffect, useMemo, useState } from "react";
import {
  AdminShipment,
  DeliveryAddress,
  KIT_TRACK_LABELS,
  KitCatalogue,
  KitItem,
  KitTrack,
  SHIPMENT_STATUS_LABELS,
  ShipmentStatus,
  useGetKitCatalogueQuery,
  useGetKitShipmentsQuery,
  useSyncKitShipmentsMutation,
  useUpdateKitCatalogueMutation,
  useUpdateKitShipmentMutation,
} from "../../app/api/kitsApi";
import { errorText } from "../../app/api/learningApi";
import {
  Button,
  Card,
  EmptyState,
  formatDate,
  formatNaira,
  PageHeader,
  Pagination,
  SearchInput,
  Select,
  Skeleton,
  TableContainer,
  Td,
  Th,
  TrackChip,
} from "./AdminShared";

import { Package, X } from "lucide-react";
const STATUS_ORDER: ShipmentStatus[] = [
  "awaiting_address",
  "ready_to_pack",
  "packed",
  "shipped",
  "delivered",
  "cancelled",
];

const STATUS_STYLE: Record<ShipmentStatus, string> = {
  awaiting_address: "bg-[#fff4f1] text-[#7a3020]",
  ready_to_pack: "bg-[#fff8ee] text-[#7a4a10]",
  packed: "bg-[#ebeff7] text-[#4a44b0]",
  shipped: "bg-[#eef6ff] text-[#1f4f8a]",
  delivered: "bg-[#edfaf4] text-[#2a7a56]",
  cancelled: "bg-[#f4f4f6] text-[#5f6780]",
};

const ACTION_LABEL: Record<ShipmentStatus, string> = {
  awaiting_address: "Back to waiting for address",
  ready_to_pack: "Mark ready to pack",
  packed: "Mark packed",
  shipped: "Mark shipped",
  delivered: "Mark delivered",
  cancelled: "Cancel package",
};

function StatusChip({ status }: { status: ShipmentStatus }) {
  return (
    <span className={`inline-flex rounded-full px-2.5 py-1 text-[12.5px] font-bold ${STATUS_STYLE[status]}`}>
      {SHIPMENT_STATUS_LABELS[status]}
    </span>
  );
}

function addressLines(a: DeliveryAddress | null) {
  if (!a) return [];
  return [
    a.recipientName,
    a.phone,
    a.line1,
    a.line2,
    a.landmark ? `Landmark: ${a.landmark}` : null,
    [a.city, a.state].filter(Boolean).join(", "),
  ].filter(Boolean) as string[];
}

function printPackingSlip(s: AdminShipment) {
  const w = window.open("", "_blank", "width=720,height=900");
  if (!w) return;
  const esc = (t: string) => t.replace(/[&<>]/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;" })[c] as string);
  w.document.write(`<!doctype html><html><head><meta charset="utf-8"><title>Packing slip — ${esc(s.childName)}</title>
<style>body{font-family:system-ui,sans-serif;margin:32px;color:#222}h1{font-size:20px;margin:0}
.muted{color:#666;font-size:13px}table{width:100%;border-collapse:collapse;margin-top:16px}
td,th{border-bottom:1px solid #ddd;padding:8px;text-align:left;font-size:14px}.box{width:18px;height:18px;border:1.5px solid #333;display:inline-block}
.addr{margin-top:16px;padding:12px;border:1px solid #ccc;border-radius:8px;font-size:15px;line-height:1.5}</style></head><body>
<h1>Future Smart Lab — ${esc(KIT_TRACK_LABELS[s.track])} kit</h1>
<p class="muted">${esc(s.childName)} · Academic year ${esc(s.academicYear)} · Package ${esc(s.id)}</p>
<div class="addr">${addressLines(s.address).map(esc).join("<br/>")}</div>
<table><thead><tr><th>Item</th><th>Qty</th><th>Packed</th></tr></thead><tbody>
${s.items.map((i) => `<tr><td>${esc(i.name)}${i.unit ? ` (${esc(i.unit)})` : ""}</td><td>${i.qty}</td><td><span class="box"></span></td></tr>`).join("")}
</tbody></table>
<p class="muted" style="margin-top:24px">Packed by: ____________________ &nbsp; Date: ____________</p>
<script>window.print()</script></body></html>`);
  w.document.close();
}

/* ─────────────────────────────────────────────────────────────────
   SHIPMENT PANEL (expanded row)
───────────────────────────────────────────────────────────────── */

const ADDRESS_FIELDS: { key: keyof DeliveryAddress; label: string }[] = [
  { key: "recipientName", label: "Recipient" },
  { key: "phone", label: "Phone" },
  { key: "line1", label: "Street address" },
  { key: "line2", label: "Address line 2" },
  { key: "landmark", label: "Landmark" },
  { key: "city", label: "City / town" },
  { key: "state", label: "State" },
];

const EMPTY_ADDRESS: DeliveryAddress = {
  recipientName: null,
  phone: null,
  line1: null,
  line2: null,
  landmark: null,
  city: null,
  state: null,
};

function ShipmentPanel({ s }: { s: AdminShipment }) {
  const [update, { isLoading }] = useUpdateKitShipmentMutation();
  const [courier, setCourier] = useState(s.courier ?? "");
  const [trackingRef, setTrackingRef] = useState(s.trackingRef ?? "");
  const [notes, setNotes] = useState(s.notes ?? "");
  const [editAddress, setEditAddress] = useState(false);
  const [address, setAddress] = useState<DeliveryAddress>(s.address ?? EMPTY_ADDRESS);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState<string | null>(null);

  useEffect(() => {
    setCourier(s.courier ?? "");
    setTrackingRef(s.trackingRef ?? "");
    setNotes(s.notes ?? "");
    setAddress(s.address ?? EMPTY_ADDRESS);
  }, [s]);

  async function save(body: Parameters<typeof update>[0]["body"], message: string) {
    setError(null);
    setSaved(null);
    try {
      await update({ id: s.id, body }).unwrap();
      setSaved(message);
      setEditAddress(false);
    } catch (err) {
      setError(errorText(err, "Could not update the package"));
    }
  }

  const details = { courier: courier || null, trackingRef: trackingRef || null, notes: notes || null };

  return (
    <div className="grid gap-5 bg-[#f9fafc] px-5 py-5 lg:grid-cols-3">
      <div>
        <p className="text-[11.5px] font-bold uppercase tracking-wider text-[#8189a0]">Deliver to</p>
        {editAddress ? (
          <div className="mt-2 space-y-2">
            {ADDRESS_FIELDS.map((f) => (
              <input
                key={f.key}
                value={address[f.key] ?? ""}
                placeholder={f.label}
                onChange={(e) => setAddress({ ...address, [f.key]: e.target.value || null })}
                className="w-full rounded-xl border border-[#dde2ec] bg-white px-3 py-2 text-xs outline-none focus:border-[#1b2d5e]"
              />
            ))}
            <div className="flex gap-2">
              <Button size="sm" disabled={isLoading} onClick={() => save({ address }, "Address saved")}>
                Save address
              </Button>
              <Button size="sm" variant="ghost" onClick={() => setEditAddress(false)}>
                Cancel
              </Button>
            </div>
          </div>
        ) : (
          <>
            {s.address && addressLines(s.address).length ? (
              <p className="mt-2 whitespace-pre-line text-sm leading-6 text-[#16213e]">
                {addressLines(s.address).join("\n")}
              </p>
            ) : (
              <p className="mt-2 text-sm text-[#7a3020]">No delivery address yet. The parent is asked for one on their Kit page.</p>
            )}
            <button
              onClick={() => setEditAddress(true)}
              className="mt-2 text-xs font-semibold text-[#24397a] hover:underline"
            >
              Edit address
            </button>
          </>
        )}
        {s.parentEmail && (
          <p className="mt-3 text-xs text-[#5f6780]">
            Parent: {s.parentName} · <a className="text-[#24397a]" href={`mailto:${s.parentEmail}`}>{s.parentEmail}</a>
          </p>
        )}
      </div>

      <div>
        <p className="text-[11.5px] font-bold uppercase tracking-wider text-[#8189a0]">
          In the box ({s.items.length})
        </p>
        <ul className="mt-2 space-y-1 text-sm text-[#16213e]">
          {s.items.map((i) => (
            <li key={i.name}>
              {i.qty > 1 ? `${i.qty} × ` : ""}
              {i.name}
              {i.unit ? ` (${i.unit})` : ""}
            </li>
          ))}
        </ul>
        {!["packed", "shipped", "delivered"].includes(s.status) && (
          <p className="mt-2 text-[12.5px] text-[#8189a0]">The list is refreshed from Kit contents when the box is marked packed.</p>
        )}
        <Button size="sm" variant="secondary" className="mt-3" onClick={() => printPackingSlip(s)} disabled={!s.addressComplete}>
          Print packing slip
        </Button>
      </div>

      <div className="space-y-2">
        <p className="text-[11.5px] font-bold uppercase tracking-wider text-[#8189a0]">Courier</p>
        <input
          value={courier}
          onChange={(e) => setCourier(e.target.value)}
          placeholder="Courier (e.g. GIG Logistics)"
          className="w-full rounded-xl border border-[#dde2ec] bg-white px-3 py-2 text-xs outline-none focus:border-[#1b2d5e]"
        />
        <input
          value={trackingRef}
          onChange={(e) => setTrackingRef(e.target.value)}
          placeholder="Tracking number"
          className="w-full rounded-xl border border-[#dde2ec] bg-white px-3 py-2 text-xs outline-none focus:border-[#1b2d5e]"
        />
        <textarea
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          placeholder="Notes (only staff see these)"
          rows={2}
          className="w-full rounded-xl border border-[#dde2ec] bg-white px-3 py-2 text-xs outline-none focus:border-[#1b2d5e]"
        />
        <div className="flex flex-wrap gap-2 pt-1">
          <Button size="sm" variant="secondary" disabled={isLoading} onClick={() => save(details, "Saved")}>
            Save details
          </Button>
          {[...s.nextStatuses].sort((a, b) => Number(isForward(s.status, b)) - Number(isForward(s.status, a))).map((next) => (
            <Button
              key={next}
              size="sm"
              variant={next === "cancelled" ? "danger" : isForward(s.status, next) ? "primary" : "ghost"}
              disabled={isLoading}
              onClick={() => {
                if (next === "cancelled" && !window.confirm(`Cancel ${s.childName}'s package?`)) return;
                save({ ...details, status: next }, `Marked ${SHIPMENT_STATUS_LABELS[next].toLowerCase()}`);
              }}
            >
              {ACTION_LABEL[next]}
            </Button>
          ))}
        </div>
        {nextHint(s)}
        {error && <p className="text-xs font-semibold text-[#9b2a2a]">{error}</p>}
        {saved && <p className="text-xs font-semibold text-[#2a7a56]">{saved}</p>}
        <div className="pt-2">
          <p className="text-[11.5px] font-bold uppercase tracking-wider text-[#8189a0]">History</p>
          <ul className="mt-1 space-y-0.5 text-[12.5px] text-[#5f6780]">
            {s.history.map((h, i) => (
              <li key={i}>
                {formatDate(h.at)} — {SHIPMENT_STATUS_LABELS[h.status as ShipmentStatus] ?? h.status}
                {h.byName ? ` · ${h.byName}` : ""}
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
}

/** Moving the package along (vs undoing a step). */
function isForward(from: ShipmentStatus, to: ShipmentStatus) {
  if (to === "cancelled") return false;
  if (from === "cancelled") return true;
  return STATUS_ORDER.indexOf(to) > STATUS_ORDER.indexOf(from);
}

function nextHint(s: AdminShipment) {
  if (s.status === "packed") {
    return <p className="text-[12.5px] text-[#8189a0]">Add the courier (and tracking number) before marking it shipped. The parent is emailed when it ships.</p>;
  }
  return null;
}

/* ─────────────────────────────────────────────────────────────────
   SHIPMENTS TAB
───────────────────────────────────────────────────────────────── */

function ShipmentsTab() {
  const [status, setStatus] = useState("");
  const [track, setTrack] = useState("");
  const [academicYear, setAcademicYear] = useState("");
  const [q, setQ] = useState("");
  const [query, setQuery] = useState("");
  const [page, setPage] = useState(1);
  const [open, setOpen] = useState<string | null>(null);
  const [sync, { isLoading: syncing }] = useSyncKitShipmentsMutation();
  const [syncMsg, setSyncMsg] = useState<string | null>(null);

  useEffect(() => {
    const t = setTimeout(() => setQuery(q.trim()), 300);
    return () => clearTimeout(t);
  }, [q]);
  useEffect(() => setPage(1), [status, track, academicYear, query]);

  const { data, isLoading, isFetching } = useGetKitShipmentsQuery({ status, track, academicYear, q: query, page });
  const d = data?.data;

  async function runSync() {
    setSyncMsg(null);
    try {
      const r = await sync({ academicYear: academicYear || undefined }).unwrap();
      setSyncMsg(
        r.data.created
          ? `Created ${r.data.created} missing package${r.data.created === 1 ? "" : "s"}.`
          : `Every paid online/hybrid learner already has a package (${r.data.checked} enrolments checked).`,
      );
    } catch (err) {
      setSyncMsg(errorText(err, "Could not check for missing packages"));
    }
  }

  return (
    <>
      <div className="mb-5 grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-6">
        {STATUS_ORDER.map((st) => (
          <button
            key={st}
            onClick={() => setStatus(status === st ? "" : st)}
            className={`rounded-[18px] border px-4 py-3 text-left transition ${status === st ? "border-[#1b2d5e] bg-[#f1f3f8]" : "border-[#e5e8ef] bg-white hover:bg-[#f9fafc]"}`}
          >
            <p className="text-[11.5px] font-bold uppercase tracking-wider text-[#8189a0]">{SHIPMENT_STATUS_LABELS[st]}</p>
            <p className="mt-0.5 text-xl font-bold text-[#16213e]">{d?.counts?.[st] ?? "—"}</p>
          </button>
        ))}
      </div>

      <div className="mb-4 flex flex-wrap items-center gap-2">
        <div className="w-56">
          <SearchInput value={q} onChange={setQ} placeholder="Child, parent, city, tracking…" />
        </div>
        <Select value={track} onChange={setTrack}>
          <option value="">All ages</option>
          {(Object.keys(KIT_TRACK_LABELS) as KitTrack[]).map((t) => (
            <option key={t} value={t}>{KIT_TRACK_LABELS[t]}</option>
          ))}
        </Select>
        <Select value={academicYear} onChange={setAcademicYear}>
          <option value="">All years</option>
          {(d?.academicYears ?? []).map((y) => (
            <option key={y} value={y}>{y}</option>
          ))}
        </Select>
        <div className="ml-auto">
          <Button size="sm" variant="secondary" disabled={syncing} onClick={runSync}>
            {syncing ? "Checking…" : "Create missing packages"}
          </Button>
        </div>
      </div>
      {syncMsg && <p className="mb-3 text-xs font-semibold text-[#3b4560]">{syncMsg}</p>}

      {isLoading ? (
        <Skeleton className="h-64 rounded-[18px]" />
      ) : !d || d.shipments.length === 0 ? (
        <EmptyState
          icon={Package}
          title={status || track || academicYear || query ? "No packages match" : "No packages yet"}
          subtitle="A package is created for every child when an online or hybrid enrolment is paid. Use “Create missing packages” to catch up on earlier enrolments."
        />
      ) : (
        <TableContainer>
          <table className={`w-full ${isFetching ? "opacity-70" : ""}`}>
            <thead>
              <tr>
                <Th>Child</Th>
                <Th>Age group</Th>
                <Th>Year</Th>
                <Th>Deliver to</Th>
                <Th>Status</Th>
                <Th>Courier</Th>
                <Th> </Th>
              </tr>
            </thead>
            <tbody>
              {d.shipments.map((s) => (
                <FragmentRow key={s.id} s={s} open={open === s.id} onToggle={() => setOpen(open === s.id ? null : s.id)} />
              ))}
            </tbody>
          </table>
          <Pagination page={d.page} pages={d.pages} total={d.total} count={d.shipments.length} onPage={setPage} />
        </TableContainer>
      )}
    </>
  );
}

function FragmentRow({ s, open, onToggle }: { s: AdminShipment; open: boolean; onToggle: () => void }) {
  return (
    <>
      <tr className="border-t border-[#ebeff7]">
        <Td>
          <p className="font-semibold text-[#16213e]">{s.childName}</p>
          <p className="text-[12.5px] text-[#8189a0]">{s.parentName ?? "—"}</p>
        </Td>
        <Td>
          <TrackChip track={s.track} />
        </Td>
        <Td>{s.academicYear}</Td>
        <Td>
          {s.addressComplete ? (
            <span className="text-sm text-[#16213e]">{[s.address?.city, s.address?.state].filter(Boolean).join(", ")}</span>
          ) : (
            <span className="text-xs font-semibold text-[#7a3020]">No address</span>
          )}
        </Td>
        <Td>
          <StatusChip status={s.status} />
          {s.receivedByParent && <p className="mt-1 text-[11.5px] text-[#2a7a56]">Parent confirmed</p>}
        </Td>
        <Td>
          <span className="text-xs text-[#3b4560]">{s.courier ?? "—"}</span>
          {s.trackingRef && <p className="text-[12.5px] text-[#8189a0]">{s.trackingRef}</p>}
        </Td>
        <Td>
          <Button size="sm" variant={open ? "secondary" : "ghost"} onClick={onToggle}>
            {open ? "Close" : "Manage"}
          </Button>
        </Td>
      </tr>
      {open && (
        <tr>
          <td colSpan={7}>
            <ShipmentPanel s={s} />
          </td>
        </tr>
      )}
    </>
  );
}

/* ─────────────────────────────────────────────────────────────────
   KIT CONTENTS TAB
───────────────────────────────────────────────────────────────── */

function KitEditor({ kit }: { kit: KitCatalogue }) {
  const [items, setItems] = useState<KitItem[]>(kit.items);
  const [title, setTitle] = useState(kit.title ?? "");
  const [packingNotes, setPackingNotes] = useState(kit.packingNotes ?? "");
  const [afterMonths, setAfterMonths] = useState(kit.shipAfterPaidMonths ?? 1);
  const [save, { isLoading }] = useUpdateKitCatalogueMutation();
  const [msg, setMsg] = useState<{ ok: boolean; text: string } | null>(null);

  useEffect(() => {
    setItems(kit.items);
    setTitle(kit.title ?? "");
    setPackingNotes(kit.packingNotes ?? "");
    setAfterMonths(kit.shipAfterPaidMonths ?? 1);
  }, [kit]);

  const dirty = useMemo(
    () =>
      JSON.stringify(items) !== JSON.stringify(kit.items) ||
      title !== (kit.title ?? "") ||
      packingNotes !== (kit.packingNotes ?? "") ||
      afterMonths !== (kit.shipAfterPaidMonths ?? 1),
    [items, title, packingNotes, afterMonths, kit],
  );

  const shipped = items.filter((i) => i.ship);
  const costed = shipped.filter((i) => i.unitCost != null);
  const total = costed.reduce((sum, i) => sum + (i.unitCost ?? 0) * i.qty, 0);

  const set = (idx: number, patch: Partial<KitItem>) =>
    setItems(items.map((it, i) => (i === idx ? { ...it, ...patch } : it)));

  async function onSave() {
    setMsg(null);
    try {
      await save({ track: kit.track, body: { title: title || null, packingNotes: packingNotes || null, shipAfterPaidMonths: afterMonths, items } }).unwrap();
      setMsg({ ok: true, text: "Saved. New packages will be packed with this list." });
    } catch (err) {
      setMsg({ ok: false, text: errorText(err, "Could not save the kit") });
    }
  }

  const cell = "w-full rounded-lg border border-[#dde2ec] bg-white px-2 py-1.5 text-xs outline-none focus:border-[#1b2d5e]";

  return (
    <Card className="p-5">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-[260px] flex-1">
          <p className="text-[11.5px] font-bold uppercase tracking-wider text-[#8189a0]">{KIT_TRACK_LABELS[kit.track]}</p>
          <input
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            className="mt-1 w-full rounded-xl border border-transparent px-1 py-1 text-lg font-bold text-[#16213e] outline-none hover:border-[#dde2ec] focus:border-[#1b2d5e]"
          />
        </div>
        <div className="text-right">
          <p className="text-xs text-[#5f6780]">{shipped.length} items in the box</p>
          <p className="text-sm font-bold text-[#16213e]">
            {costed.length ? `≈ ${formatNaira(total)} per child` : "No costs entered"}
          </p>
          {costed.length > 0 && costed.length < shipped.length && (
            <p className="text-[12.5px] text-[#8189a0]">{shipped.length - costed.length} items have no cost yet</p>
          )}
        </div>
      </div>

      <div className="mt-4 overflow-x-auto">
        <table className="w-full min-w-[820px]">
          <thead>
            <tr className="text-left text-[11.5px] font-bold uppercase tracking-wider text-[#8189a0]">
              <th className="px-1 py-2">In box</th>
              <th className="px-1 py-2">Item</th>
              <th className="px-1 py-2 w-16">Qty</th>
              <th className="px-1 py-2 w-24">Unit</th>
              <th className="px-1 py-2">Used in</th>
              <th className="px-1 py-2 w-28">Cost each (₦)</th>
              <th className="px-1 py-2 w-8" />
            </tr>
          </thead>
          <tbody>
            {items.map((it, i) => (
              <tr key={it.id ?? `new-${i}`} className={`border-t border-[#ebeff7] ${it.ship ? "" : "opacity-55"}`}>
                <td className="px-1 py-1.5">
                  <input type="checkbox" checked={it.ship} onChange={(e) => set(i, { ship: e.target.checked })} title="Pack this item" />
                </td>
                <td className="px-1 py-1.5">
                  <input className={cell} value={it.name} onChange={(e) => set(i, { name: e.target.value })} />
                </td>
                <td className="px-1 py-1.5">
                  <input className={cell} type="number" min={1} value={it.qty} onChange={(e) => set(i, { qty: Number(e.target.value) || 1 })} />
                </td>
                <td className="px-1 py-1.5">
                  <input className={cell} value={it.unit ?? ""} placeholder="—" onChange={(e) => set(i, { unit: e.target.value || null })} />
                </td>
                <td className="px-1 py-1.5">
                  <input className={cell} value={it.usedIn ?? ""} placeholder="Which lessons use it" onChange={(e) => set(i, { usedIn: e.target.value || null })} />
                </td>
                <td className="px-1 py-1.5">
                  <input
                    className={cell}
                    type="number"
                    min={0}
                    value={it.unitCost ?? ""}
                    placeholder="—"
                    onChange={(e) => set(i, { unitCost: e.target.value === "" ? null : Number(e.target.value) })}
                  />
                </td>
                <td className="px-1 py-1.5 text-center">
                  <button
                    onClick={() => setItems(items.filter((_, j) => j !== i))}
                    className="text-sm text-[#a6adbf] hover:text-[#9b2a2a]"
                    title="Remove item"
                  >
                    <X className="inline-block h-[1em] w-[1em] align-[-0.125em]" aria-hidden />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <button
        onClick={() => setItems([...items, { name: "", qty: 1, unit: null, usedIn: null, note: null, unitCost: null, ship: true }])}
        className="mt-2 text-xs font-semibold text-[#24397a] hover:underline"
      >
        + Add item
      </button>

      <label className="mt-4 block text-[11.5px] font-bold uppercase tracking-wider text-[#8189a0]">Packing notes</label>
      <textarea
        value={packingNotes}
        onChange={(e) => setPackingNotes(e.target.value)}
        rows={2}
        className="mt-1 w-full rounded-xl border border-[#dde2ec] bg-white px-3 py-2 text-xs outline-none focus:border-[#1b2d5e]"
      />

      <label className="mt-4 block text-[11.5px] font-bold uppercase tracking-wider text-[#8189a0]">When the package goes out</label>
      <div className="mt-1 flex flex-wrap items-center gap-2 text-xs text-[#3b4560]">
        <span>After</span>
        <select
          value={afterMonths}
          onChange={(e) => setAfterMonths(Number(e.target.value))}
          className="rounded-lg border border-[#dde2ec] bg-white px-2 py-1.5 text-xs outline-none focus:border-[#1b2d5e]"
          aria-label="Paid months before the package goes out"
        >
          {[1, 2, 3, 4, 6, 9, 12].map((n) => (
            <option key={n} value={n}>{n === 1 ? "the first payment" : `${n} paid months`}</option>
          ))}
        </select>
        <span className="text-[#8189a0]">
          of the academic year. Quarterly payers reach 3 months in one payment; annual payers reach 12.
        </span>
      </div>

      <div className="mt-3 flex items-center gap-3">
        <Button size="sm" disabled={!dirty || isLoading} onClick={onSave}>
          {isLoading ? "Saving…" : "Save kit"}
        </Button>
        {dirty && (
          <Button size="sm" variant="ghost" onClick={() => setItems(kit.items)}>
            Undo changes
          </Button>
        )}
        {msg && <p className={`text-xs font-semibold ${msg.ok ? "text-[#2a7a56]" : "text-[#9b2a2a]"}`}>{msg.text}</p>}
      </div>
    </Card>
  );
}

function ContentsTab() {
  const { data, isLoading } = useGetKitCatalogueQuery();
  if (isLoading || !data) return <Skeleton className="h-96 rounded-[18px]" />;
  return (
    <div className="space-y-5">
      <p className="text-sm text-[#5f6780]">
        Unticked items stay on the list but are not packed: for example, the Ages 9–12 Arduino set, which waits for its lessons to be
        written. Packages take a copy of this list when they are marked packed.
      </p>
      {data.data.map((kit) => (
        <KitEditor key={kit.track} kit={kit} />
      ))}
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────────
   PAGE
───────────────────────────────────────────────────────────────── */

export default function AdminKits() {
  const [tab, setTab] = useState<"shipments" | "contents">("shipments");
  return (
    <>
      <PageHeader
        title="Kits"
        subtitle="Every online learner is posted a hardware package once a year, with everything their lessons need."
      />
      <div className="mb-5 flex gap-2">
        {(
          [
            ["shipments", "Shipments"],
            ["contents", "Kit contents"],
          ] as const
        ).map(([key, label]) => (
          <button
            key={key}
            onClick={() => setTab(key)}
            className={`rounded-2xl px-4 py-2 text-sm font-semibold transition ${tab === key ? "bg-[#1b2d5e] text-white" : "border border-[#dde2ec] text-[#3b4560] hover:bg-[#f1f3f8]"}`}
          >
            {label}
          </button>
        ))}
      </div>
      {tab === "shipments" ? <ShipmentsTab /> : <ContentsTab />}
    </>
  );
}
